import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";

import { TOOTH_CODE_MAPPING } from "@/App/constants/constants";

import { AuthenticationContext } from "../context";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { ClaimsTypes } from "../constants/constants";

export const getMedicalClaimDetails = async (authToken: string, body: Partial<ViewRequest> & { id: number }) => {
  return request<typeof body, MedicalClaimGetObject>(
    {
      endpoint: "/medical_claim/get",
      body: { ...body },
    },
    authToken,
  );
};

export const useGetMedicalClaimDetails = (body: MedicalClaimGetRequest, enabled?: boolean) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getMedicalClaimDetails(await getAuthToken(), {
          ...body,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return getMedicalClaimDetails(await getAuthToken(), { ...body });
      }
    },
    queryKey: ["medicalClaimDetails", body.id, ...getEligibilityBasedQueryKey(memberEligibility)],
    enabled: enabled,
  });

  return query;
};

export const listMedicalClaims = async (authToken: string, body?: Partial<ViewRequest> & MedicalClaimListRequest) => {
  return request<typeof body, MedicalClaimListResponse>(
    {
      endpoint: "/medical_claim/list",
      body: { ...body },
    },
    authToken,
  );
};

export const useListMedicalClaimsQuery = (body: MedicalClaimListRequest, enabled?: boolean) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMedicalClaims(await getAuthToken(), {
          ...body,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listMedicalClaims(await getAuthToken(), { ...body });
      }
    },
    queryKey: ["medicalClaimList", body.type, body?.page_number || 1, ...getEligibilityBasedQueryKey(memberEligibility)],
    enabled: enabled,
  });

  return query;
};

export type Status = "denied" | "paid" | "partially_paid" | "pended";
type ServiceLineItemStatus = Exclude<Status, "partially_paid"> | "reversal";

interface OrderBy {
  readonly minimum_service_date?: string | null;
  readonly due_date?: string | null;
  readonly id?: string | null;
  readonly medical_claim_id?: string | null;
  readonly pend_reasons?: string | null;
  readonly status?: string | null;
  readonly total_patient_pay_amount?: string | null;
  readonly total_claim_charge_amount?: string | null;
  readonly total_plan_pay_amount?: string | null;
  readonly type?: string | null;
}

export interface MedicalClaimGetRequest {
  readonly id: number;
  readonly person_code?: string;
}
export interface MedicalClaimListRequest {
  readonly person_code?: string;
  readonly full_text_search?: string;
  readonly status?: Status[] | null;
  readonly network?: "out_of_network" | "in_network_tier_1" | null;
  readonly order_by?: OrderBy[] | null;
  readonly month?: number | null;
  readonly year?: number | null;
  readonly year_to_date?: boolean | null;
  readonly page_number?: number | null;
  readonly results_per_page?: number | null;
  readonly type?: Omit<ClaimsTypes, "pharmacy"> | null;
}

export interface MedicalClaimListObject {
  readonly id: number;
  readonly status?: Status | null;
  readonly network?: "out_of_network" | "in_network_tier_1" | null;
  readonly received_at?: string | null;
  readonly submission_date?: string | null;
  readonly start_date?: string | null;
  readonly end_date?: string | null;
  readonly minimum_service_date?: string | null;
  readonly maximum_service_date?: string | null;
  readonly billing_provider_first_name?: string | null;
  readonly billing_provider_last_name_or_organizational_name?: string | null;
  readonly total_patient_pay_amount?: number | null;
  readonly client_id?: string | null;
  readonly external_member_id?: string | null;
  readonly person_code?: string | null;
  readonly direct_member_reimbursement_indicator?: string | null;
}

export type ToothCode = keyof typeof TOOTH_CODE_MAPPING;

export interface ServiceLineItem {
  readonly amount_applied_to_deductible?: number | null,
  readonly service_category?: string | null,
  readonly service_date?: string | null,
  readonly status?: ServiceLineItemStatus | null,
  readonly preventive_category?: string | null,
  readonly line_item_charge_amount?: number | null,
  readonly patient_pay_amount?: number | null,
  readonly plan_pay_amount?: number | null,
  readonly repriced_allowed_amount?: number | null,
  readonly procedure_code?: string | null,
  readonly tooth_code_surface?: ToothCode[] | null,
}

export interface MedicalClaimGetObject extends MedicalClaimListObject {
  readonly billing_provider_address_line_1?: string,
  readonly billing_provider_address_line_2?: string,
  readonly billing_provider_city?: string,
  readonly billing_provider_state?: string,
  readonly billing_provider_zip?: string,
  readonly rendering_provider_first_name?: string,
  readonly rendering_provider_last_name_or_organizational_name?: string,
  readonly service_line_items: ServiceLineItem[],
  readonly total_amount_applied_to_deductible: number | null,
  readonly total_plan_pay_amount?: number | null,
  readonly total_amount_not_covered?: number | null,
  readonly total_amount_of_coinsurance?: number | null,
  readonly total_amount_of_copay?: number | null,
  readonly direct_member_reimbursement_indicator?: string | null,
}

interface MedicalClaimListResponse {
  results: {
    medical_claim: MedicalClaimListObject;
  }[];
  result_count: number;
}
